<script setup lang="ts">
import { ref, computed } from 'vue'
import axios from 'axios'
import DataTable from './datatable/DataTable.vue'
import GefahrstoffForm from './GefahrstoffForm.vue'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Glasses, AlertTriangle, Wind, SprayCan, Biohazard, X, FileSpreadsheet, Loader2, Copy } from 'lucide-vue-next'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useToast } from '@/components/ui/toast'
import { generateExcel, get_data_attr, optionLabel } from '@/utils/globals'
import { useI18n } from 'vue-i18n'
import { useViewStore } from '@/stores/viewStore'
import DangeroursMaterialsLogo from '@/components/images/biohazard.svg'

const { t } = useI18n()
const { toast } = useToast()
const viewStore = useViewStore()
const specsInfo = get_data_attr('specs')
const userInfo = get_data_attr('user')

const selectedGefahrstoff = ref<any | null>(null)
const gefahrstoffeTable = ref<any | null>(null)
const isExporting = ref(false)
const formMode = ref<'new' | 'edit' | 'duplicate' | null>(null)
const editingGefahrstoffId = ref<number | undefined>(undefined)
const duplicateGefahrstoffId = ref<number | undefined>(undefined)
const savingRowId = ref<string | number | null>(null)
const pendingRollbackRow = ref<any | null>(null)

function setSelectedGefahrstoff(row: any) {
  selectedGefahrstoff.value = row
}

const formSheetOpen = computed({
  get: () => formMode.value !== null,
  set: (open: boolean) => {
    if (!open) closeFormSheet()
  }
})

const formSheetTitle = computed(() => {
  if (formMode.value === 'duplicate') return 'Gefahrstoff duplizieren'
  return formMode.value === 'edit' ? 'Gefahrstoff bearbeiten' : 'Neuer Gefahrstoff'
})

const formKey = computed(() => {
  if (formMode.value === 'duplicate') return `duplicate-${duplicateGefahrstoffId.value}`
  return formMode.value === 'edit' ? `edit-${editingGefahrstoffId.value}` : 'new'
})

function openCreateSheet() {
  if (savingRowId.value) return
  formMode.value = 'new'
  editingGefahrstoffId.value = undefined
  duplicateGefahrstoffId.value = undefined
  pendingRollbackRow.value = null
}

function openEditSheet(row: any) {
  if (savingRowId.value || !row?.id) return
  formMode.value = 'edit'
  editingGefahrstoffId.value = Number(row.id)
  duplicateGefahrstoffId.value = undefined
  pendingRollbackRow.value = null
}

function openDuplicateSheet(row: any) {
  if (savingRowId.value || !row?.id) return
  formMode.value = 'duplicate'
  editingGefahrstoffId.value = undefined
  duplicateGefahrstoffId.value = Number(row.id)
  pendingRollbackRow.value = null
}

function closeFormSheet() {
  if (savingRowId.value) return
  formMode.value = null
  editingGefahrstoffId.value = undefined
  duplicateGefahrstoffId.value = undefined
  pendingRollbackRow.value = null
}

function updateSelectedGefahrstoff(rowId: string | number, row: any) {
  if (!selectedGefahrstoff.value || selectedGefahrstoff.value.id !== rowId) return
  selectedGefahrstoff.value = { ...selectedGefahrstoff.value, ...row }
}

function handleFormSaveStart(payload: any) {
  const isEdit = payload?.isEditMode && payload?.id != null
  const optimisticRow = payload?.row || {}

  if (isEdit) {
    const rowId = payload.id
    pendingRollbackRow.value = gefahrstoffeTable.value?.snapshotRow(rowId) || null
    savingRowId.value = rowId
    gefahrstoffeTable.value?.optimisticReplaceRow(rowId, optimisticRow)
    updateSelectedGefahrstoff(rowId, optimisticRow)
    return
  }

  const tempId = `new-gefahrstoff-${Date.now()}`
  pendingRollbackRow.value = null
  savingRowId.value = tempId
  gefahrstoffeTable.value?.prependTemporaryRow({
    ...optimisticRow,
    id: tempId,
    isNew: true
  })
}

function handleFormSaved(payload: any) {
  const row = payload?.row
  const rowId = savingRowId.value || payload?.id || row?.id

  if (row && rowId != null) {
    gefahrstoffeTable.value?.replaceLocalRow(rowId, row)
    if (selectedGefahrstoff.value && (selectedGefahrstoff.value.id === rowId || selectedGefahrstoff.value.id === row.id)) {
      selectedGefahrstoff.value = row
    }
    if (formMode.value === 'duplicate') {
      selectedGefahrstoff.value = row
    }
  }

  savingRowId.value = null
  pendingRollbackRow.value = null
  formMode.value = null
  editingGefahrstoffId.value = undefined
  duplicateGefahrstoffId.value = undefined
}

function handleFormSaveError() {
  const rowId = savingRowId.value
  if (rowId != null) {
    gefahrstoffeTable.value?.rollbackLocalRow(rowId, pendingRollbackRow.value)
    if (pendingRollbackRow.value && selectedGefahrstoff.value?.id === rowId) {
      selectedGefahrstoff.value = pendingRollbackRow.value
    }
  }
  savingRowId.value = null
  pendingRollbackRow.value = null
}

function openBatchUpdate() {
  viewStore.setActionView('registry.gefahrstoffe.batch')
}

async function exportGefahrstoffe() {
  if (isExporting.value) return

  const params = { ...(gefahrstoffeTable.value?.currentQueryParams?.() || {}) }
  delete params.page
  delete params.per_page
  delete params.reset_filter
  delete params.reset_filter_button

  const exportColumns = (gefahrstoffeTable.value?.currentExportColumns?.() || [])
    .filter((column: any) => column?.key)

  await generateExcel(axios, '/gefahrstoffe/export.xlsx', toast, isExporting, {
    method: 'post',
    data: {
      ...params,
      columns: exportColumns,
    },
  })
}

function getNested(obj: any, path: string) {
  if (!obj || !path) return undefined
  const parts = path.split('.')
  return parts.reduce((acc, key) => (acc ? acc[key] : undefined), obj)
}

function formatValue(column: any, value: any) {
  if (value === null || value === undefined || value === '') return '–'

  // Many-to-many collections
  if (column.type === 'many') {
    const list = Array.isArray(value) ? value : Object.values(value)
    if (!list.length) return '–'
    return list.map((item: any) => optionLabel(column.options || [], item)).join(', ')
  }

  // Select / multi-select
  if (column.type === 'select' || column.type === 'multi-select') {
    return optionLabel(column.options || [], value)
  }

  // Boolean
  if (column.type === 'boolean') {
    return value ? t('view.yes') : t('view.no')
  }

  // Dates
  if (column.type === 'date' && typeof value === 'string') {
    return value.split('T')[0]
  }

  // Fallback
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const gefahrstoffeColumns = computed(() => specsInfo?.gefahrstoffe || [])

function getColumn(key: string) {
  const direct = gefahrstoffeColumns.value.find((c: any) => c.key === key)
  if (direct) return direct

  // Backend specs still define Standort as `standort_id`, while detail payload exposes `standort` label text.
  if (key === 'standort') {
    return gefahrstoffeColumns.value.find((c: any) => c.key === 'standort_id')
  }

  return undefined
}

function fieldValue(key: string) {
  if (!selectedGefahrstoff.value) return undefined
  const col = getColumn(key)
  const value = getNested(selectedGefahrstoff.value, key)
  return col ? formatValue(col, value) : (value ?? '–')
}

// Section 1: Identification & hazard info (UN-Nr., EAK, H/P/EUH, Signalwort, CAS, Kommentar, Flammpunkt, WGK, Lagerklasse, GHS)
const section1Keys = [
  'un_number',
  'eak_number',
  'h_phrases',
  'p_phrases',
  'eurh_phrases',
  'h_phrases_signal_word',
  'cas_numbers',
  'flammpunkt',
  'water_hazard_class',
  'inventory_class',
  'comment',
]

// Section 2: Verwendung & Lagerung
const section2Keys = [
  'active',
  'use',
  'use_start',
  'use_end',
  'use_duration_periodicity',
  'workspace',
  'number_of_workers',
  'standort',
  'use_location_or_machinery',
  'inventory_space_name',
  'inventory_space_area_name',
  'inventory_space_type',
  'inventory_size',
  'yearly_usage',
  'extinguishing_agent_suitable',
  'extinguishing_agent_unsuitable',
  'inventory_conditions',
  'gebinde_number',
]

// Section 3: Physikalisch-chemische Eigenschaften
const section3Keys = [
  'physical_state_at_room_temperature',
  'form',
  'color',
  'odor',
  'solubility_in_water',
  'melting_point',
  'boiling_point',
  'ignition_temperature',
  'autoignition_temperature',
  'decomposition_point',
  'density',
  'viscosity',
  'vapor_pressure',
  'relative_vapor_density',
  'ph_value',
  'lower_explosion_limit_vol_percent',
  'upper_explosion_limit_vol_percent',
  'reactivity',
  'stability',
]

const ghsPictogramUrls = computed(() => {
  // Prefer pictograms explicitly assigned via gefahrstoffe_ghs (backend: ghs_pictograms_urls)
  const directUrls = getNested(selectedGefahrstoff.value, 'ghs_pictograms_urls')
  if (Array.isArray(directUrls) && directUrls.length) return directUrls

  // Fallback for endpoints that embed ghs objects
  const ghs = getNested(selectedGefahrstoff.value, 'ghs')
  if (Array.isArray(ghs) && ghs.length) {
    return ghs
      .map((g: any) => g?.pictogram_url)
      .filter((u: any) => typeof u === 'string' && u.length > 0)
  }

  return []
})
</script>

<template>
  <div class="flex h-screen w-full">
    <div class="flex w-full p-4 gap-4">
      <!-- Left: Gefahrstoffe table -->
      <div
        class="flex-1 transition-[max-width] duration-300 ease-in-out"
        :class="selectedGefahrstoff ? 'max-w-[50%]' : 'max-w-full'"
      >
        <DataTable
          ref="gefahrstoffeTable"
          label="view.gefahrstoffe"
          labelSingular="view.gefahrstoff"
          :logo="null"
          endpoint="gefahrstoffe"
          :columns="specsInfo['gefahrstoffe']"
          :enableDetail="false"
          @request-add="openCreateSheet"
          @request-edit="openEditSheet"
        >
          <template #logo>
            <DangeroursMaterialsLogo class="w-8 h-8" />
          </template>

          <template #extraactions="{ row }">
            <Button
              :id="'gefahrstoff-duplicate-' + row.id"
              title="Duplizieren"
              size="icon"
              variant="ghost"
              class="text-black-600 hover:bg-blue-50"
              @click.stop
              @click="() => openDuplicateSheet(row)"
            >
              <Copy class="w-4 h-4" />
            </Button>
            <Button
              :id="'gefahrstoff-detail-' + row.id"
              :title="t('view.btn_detail')"
              size="icon"
              variant="ghost"
              class="text-black-600 hover:bg-blue-50"
              @click="() => setSelectedGefahrstoff(row)"
            >
              <Glasses class="w-4 h-4" />
            </Button>
          </template>
          <template #extraglobalactions>
            <Button
              v-if="userInfo.is_hoeppner"
              variant="outline"
              :disabled="isExporting"
              @click="exportGefahrstoffe"
            >
              <Loader2 v-if="isExporting" class="mr-2 h-4 w-4 animate-spin" />
              <FileSpreadsheet v-else class="mr-2 h-4 w-4" />
              Excel Export
            </Button>
            <Button
              v-if="userInfo.is_hoeppner"
              variant="outline"
              @click="openBatchUpdate"
            >
              Batch-Update Gefahrstoffe
            </Button>
          </template>
        </DataTable>
      </div>

      <!-- Right: Detail sheet -->
      <div class="flex-1" v-if="selectedGefahrstoff">
        <Card class="h-full overflow-auto flex flex-col">
          <div class="flex items-center justify-between p-4 border-b bg-gray-50">
            <div class="flex items-center gap-2">
              <h2 class="text-xl font-bold">
                {{ selectedGefahrstoff?.bezeichnung || selectedGefahrstoff?.name || selectedGefahrstoff?.id }}
              </h2>
              <Tooltip>
                <TooltipTrigger as-child>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    class="shrink-0 text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                    :title="t('view.close')"
                    @click="setSelectedGefahrstoff(null)"
                  >
                    <X class="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {{ t('view.close') }}
                </TooltipContent>
              </Tooltip>
            </div>
            <div class="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger as-child>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    class="shrink-0 text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                    title="Duplizieren"
                    @click="openDuplicateSheet(selectedGefahrstoff)"
                  >
                    <Copy class="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Duplizieren
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="inline-flex" :class="selectedGefahrstoff?.hazardous_material ? 'text-green-600' : 'text-gray-400'">
                    <AlertTriangle class="w-5 h-5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {{ selectedGefahrstoff?.hazardous_material ? 'Gefahrstoff' : 'Kein Gefahrstoff' }}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="inline-flex" :class="selectedGefahrstoff?.gas ? 'text-green-600' : 'text-gray-400'">
                    <Wind class="w-5 h-5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {{ selectedGefahrstoff?.gas ? 'Gas' : 'Kein Gas' }}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="inline-flex" :class="selectedGefahrstoff?.spray ? 'text-green-600' : 'text-gray-400'">
                    <SprayCan class="w-5 h-5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {{ selectedGefahrstoff?.spray ? 'Spray' : 'Kein Spray' }}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger as-child>
                  <span class="inline-flex" :class="selectedGefahrstoff?.carcinogenic_material ? 'text-green-600' : 'text-gray-400'">
                    <Biohazard class="w-5 h-5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {{ selectedGefahrstoff?.carcinogenic_material ? 'Krebserzeugend' : 'Nicht Krebserzeugend' }}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <div class="p-4 overflow-auto space-y-6 text-sm">
            <!-- Section 1: Kennzeichnung & Gefahren -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Kennzeichnung & Gefahren
              </h3>
              <div class="grid grid-cols-2 gap-x-6 gap-y-3">
                <div
                  v-for="key in section1Keys"
                  :key="key"
                  class="flex flex-col"
                >
                  <div class="font-semibold text-gray-700">
                    {{ getColumn(key)?.label ?? key }}
                  </div>
                  <div class="text-gray-900 break-words">
                    {{ fieldValue(key) }}
                  </div>
                </div>
              </div>
              <div class="flex flex-col mt-2">
                <div class="font-semibold text-gray-700 mb-2">
                  GHS-Piktogramme
                </div>
                <div v-if="ghsPictogramUrls.length" class="flex flex-wrap gap-2">
                  <img
                    v-for="(url, idx) in ghsPictogramUrls"
                    :key="idx"
                    :src="url"
                    alt="GHS pictogram"
                    class="w-12 h-12 object-contain"
                  />
                </div>
                <div v-else class="text-gray-900">
                  -
                </div>
              </div>
            </section>

            <!-- Section 2: Verwendung & Lagerung -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Verwendung & Lagerung
              </h3>
              <div class="grid grid-cols-2 gap-x-6 gap-y-3">
                <div
                  v-for="key in section2Keys"
                  :key="key"
                  class="flex flex-col"
                >
                  <div class="font-semibold text-gray-700">
                    {{ getColumn(key)?.label ?? key }}
                  </div>
                  <div class="text-gray-900 break-words">
                    {{ fieldValue(key) }}
                  </div>
                </div>
              </div>
            </section>

            <!-- Section 3: Physikalisch-chemische Eigenschaften -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Physikalisch-chemische Eigenschaften
              </h3>
              <div class="grid grid-cols-2 gap-x-6 gap-y-3">
                <div
                  v-for="key in section3Keys"
                  :key="key"
                  class="flex flex-col"
                >
                  <div class="font-semibold text-gray-700">
                    {{ getColumn(key)?.label ?? key }}
                  </div>
                  <div class="text-gray-900 break-words">
                    {{ fieldValue(key) }}
                  </div>
                </div>
              </div>
            </section>

            <!-- Section 4: Substitutionsprüfung -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Substitutionsprüfung
              </h3>
              <div
                v-if="selectedGefahrstoff?.substitutiontest"
                class="grid grid-cols-2 gap-x-6 gap-y-3 text-gray-900"
              >
                <template v-if="selectedGefahrstoff?.substitutiontest">
                  <div class="flex flex-col">
                    <div class="font-semibold text-gray-700">Nächste Prüfung</div>
                    <div>{{ selectedGefahrstoff.substitutiontest.next_check ? selectedGefahrstoff.substitutiontest.next_check.split('T')[0] : '–' }}</div>
                  </div>
                  <div class="flex flex-col">
                  </div>
                  <div class="flex flex-col">
                    <div class="font-semibold text-gray-700">Verantwortlich</div>
                    <div>{{ selectedGefahrstoff.substitutiontest.name_responsible || '–' }}</div>
                  </div>
                  <div class="flex flex-col">
                    <div class="font-semibold text-gray-700">E-Mail Verantwortlicher</div>
                    <div>{{ selectedGefahrstoff.substitutiontest.email_responsible || '–' }}</div>
                  </div>
                  <div class="flex flex-col">
                    <div class="font-semibold text-gray-700">E-Mail Abteilung</div>
                    <div>{{ selectedGefahrstoff.substitutiontest.email_department || '–' }}</div>
                  </div>
                  <div class="flex flex-col col-span-2">
                    <div class="font-semibold text-gray-700">Ergebnis</div>
                    <div class="break-words">{{ selectedGefahrstoff.substitutiontest.result || '–' }}</div>
                  </div>
                </template>
              </div>
              <p v-else class="text-gray-500">Keine Substitutionsprüfung hinterlegt.</p>
            </section>

            <!-- Section 4: Sicherheitsdatenblatt -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Sicherheitsdatenblatt
              </h3>
              <div
                v-if="selectedGefahrstoff?.datasheet"
                class="grid grid-cols-2 gap-x-6 gap-y-3 text-gray-900"
              >
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Nächste Prüfung</div>
                  <div>{{ selectedGefahrstoff.datasheet.next_check ? selectedGefahrstoff.datasheet.next_check.split('T')[0] : '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Hersteller</div>
                  <div>{{ selectedGefahrstoff.datasheet.manufacturer || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Verantwortlich</div>
                  <div>{{ selectedGefahrstoff.datasheet.name_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Verantwortlicher</div>
                  <div>{{ selectedGefahrstoff.datasheet.email_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Abteilung</div>
                  <div>{{ selectedGefahrstoff.datasheet.email_department || '–' }}</div>
                </div>
                <div class="flex flex-col col-span-2">
                  <a
                    v-if="selectedGefahrstoff.datasheet.datasheet_url"
                    :href="selectedGefahrstoff.datasheet.datasheet_url"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline"
                  >
                    Sicherheitsdatenblatt öffnen
                  </a>
                  <span v-else class="text-gray-500">Kein Dokument verknüpft.</span>
                </div>
              </div>
              <p v-else class="text-gray-500">Kein Sicherheitsdatenblatt hinterlegt.</p>
            </section>

            <!-- Section 5: GBU -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Gefährdungsbeurteilung
              </h3>
              <div
                v-if="selectedGefahrstoff?.gbu"
                class="grid grid-cols-2 gap-x-6 gap-y-3 text-gray-900"
              >
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Nächste Prüfung</div>
                  <div>{{ selectedGefahrstoff.gbu.next_check ? selectedGefahrstoff.gbu.next_check.split('T')[0] : '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Kennung</div>
                  <div>{{ selectedGefahrstoff.gbu.identifier || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Verantwortlich</div>
                  <div>{{ selectedGefahrstoff.gbu.name_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Verantwortlicher</div>
                  <div>{{ selectedGefahrstoff.gbu.email_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Abteilung</div>
                  <div>{{ selectedGefahrstoff.gbu.email_department || '–' }}</div>
                </div>
                <div class="flex flex-col col-span-2">
                  <a
                    v-if="selectedGefahrstoff.gbu.gbu_url"
                    :href="selectedGefahrstoff.gbu.gbu_url"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline"
                  >
                    GBU öffnen
                  </a>
                  <span v-else class="text-gray-500">Kein Dokument verknüpft.</span>
                </div>
              </div>
              <p v-else class="text-gray-500">Keine GBU hinterlegt.</p>
            </section>

            <!-- Section 6: Betriebsanweisung -->
            <section class="space-y-3">
              <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-1">
                Betriebsanweisung
              </h3>
              <div
                v-if="selectedGefahrstoff?.betriebsanweisung"
                class="grid grid-cols-2 gap-x-6 gap-y-3 text-gray-900"
              >
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Nächste Prüfung</div>
                  <div>{{ selectedGefahrstoff.betriebsanweisung.next_check ? selectedGefahrstoff.betriebsanweisung.next_check.split('T')[0] : '–' }}</div>
                </div>
                <div class="flex flex-col">
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">Verantwortlich</div>
                  <div>{{ selectedGefahrstoff.betriebsanweisung.name_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Verantwortlicher</div>
                  <div>{{ selectedGefahrstoff.betriebsanweisung.email_responsible || '–' }}</div>
                </div>
                <div class="flex flex-col">
                  <div class="font-semibold text-gray-700">E-Mail Abteilung</div>
                  <div>{{ selectedGefahrstoff.betriebsanweisung.email_department || '–' }}</div>
                </div>
                <div class="flex flex-col col-span-2">
                  <a
                    v-if="selectedGefahrstoff.betriebsanweisung.betriebsanweisung_url"
                    :href="selectedGefahrstoff.betriebsanweisung.betriebsanweisung_url"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline"
                  >
                    Betriebsanweisung öffnen
                  </a>
                  <span v-else class="text-gray-500">Kein Dokument verknüpft.</span>
                </div>
              </div>
              <p v-else class="text-gray-500">Keine Betriebsanweisung hinterlegt.</p>
            </section>
          </div>
        </Card>
      </div>
    </div>

    <Sheet v-model:open="formSheetOpen">
      <SheetContent
        side="right"
        class="z-[2500] max-h-[95vh] w-[min(1120px,96vw)] overflow-y-auto p-6 sm:max-w-none"
        overlay-class="z-[2490]"
        :disable-outside-pointer-events="false"
      >
        <SheetHeader class="mb-4">
          <SheetTitle>{{ formSheetTitle }}</SheetTitle>
        </SheetHeader>
        <GefahrstoffForm
          v-if="formMode"
          :key="formKey"
          embedded
          :id="editingGefahrstoffId"
          :duplicate-from-id="duplicateGefahrstoffId"
          @save-start="handleFormSaveStart"
          @saved="handleFormSaved"
          @save-error="handleFormSaveError"
          @cancel="closeFormSheet"
        />
      </SheetContent>
    </Sheet>
  </div>
</template>
